'use client';
import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';

interface RewardModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** 奖励金额 */
  amount?: number;
  /** 活动结束时间（时间戳 ms） */
  endTime?: number;
  /** 活动是否进行中，undefined 表示无活动 */
  active?: boolean;
}

/** 计算倒计时 */
function calcCountdown(endTime: number) {
  const diff = Math.max(0, endTime - Date.now());
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / (1000 * 60)) % 60),
    seconds: Math.floor((diff / 1000) % 60),
    isExpired: diff <= 0,
  };
}

const RewardModal: React.FC<RewardModalProps> = ({
  isOpen,
  onClose,
  amount = 0,
  endTime,
  active,
}) => {
  const { t } = useTranslation();
  const [countdown, setCountdown] = useState(() =>
    calcCountdown(endTime || 0)
  );

  // 倒计时定时器
  useEffect(() => {
    if (!isOpen || !endTime) return;
    setCountdown(calcCountdown(endTime));
    const timer = setInterval(() => {
      const next = calcCountdown(endTime);
      setCountdown(next);
      if (next.isExpired) clearInterval(timer);
    }, 1000);
    return () => clearInterval(timer);
  }, [isOpen, endTime]);
  /** 格式化数字为两位 */
  const pad = (n: number) => String(n).padStart(2, '0');

  if (!isOpen) return null;

  return (
    <div className="fixed right-0 bottom-12 z-[50] flex items-center justify-center">
      <div className="relative w-[320px] h-[227px] mx-4 rounded-xl overflow-hidden shadow-2xl border border-button-border/50 bg-background">
        {/* 背景图 */}
        <img
          src="/imgs/reward-bg.png"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />

        {/* 内容 */}
        <div className="relative p-4 h-full flex flex-col">
          {/* 关闭按钮 */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 text-font-primary/40 hover:text-font-primary/80 transition-colors cursor-pointer"
          >
            <X className="w-4 h-4" />
          </button>

          {/* 标题行 */}
          <div className="flex items-center gap-2 mb-2">
            <span className="text-font-primary text-base font-bold italic">
              {t('rewardPlan.title')}
            </span>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-medium ${
              active === true ? 'border-primary/60 text-primary'
                : active === false ? 'border-white/30 text-font-primary/50'
                : 'border-white/20 text-font-primary/40'
            }`}>
              <span className={`w-1.5 h-1.5 rounded-full ${active === true ? 'bg-primary' : 'bg-white/40'}`} />
              {active === true ? t('rewardPlan.recruiting')
                : active === false ? t('rewardPlan.ended')
                : t('rewardPlan.noActivity')}
            </span>
          </div>

          {/* 金额 */}
          <div className="mb-2">
            <span className="text-primary text-3xl font-extrabold">
              ${amount.toLocaleString()}
            </span>
          </div>

          {/* 倒计时 */}
          <div className="mb-3">
            <span className="text-font-primary/50 text-xs">{t('rewardPlan.endsIn')}</span>
            <div className="flex items-center gap-1.5 mt-1">
              {[
                { value: countdown.days, label: t('rewardPlan.days') },
                { value: countdown.hours, label: t('rewardPlan.hours') },
                { value: countdown.minutes, label: t('rewardPlan.minutes') },
                { value: countdown.seconds, label: t('rewardPlan.seconds') },
              ].map((item, i) => (
                <div
                  key={i}
                  className="flex items-center justify-center min-w-[44px] h-7 rounded-md border border-white/20 bg-white/5 text-font-primary text-xs font-medium"
                >
                  {pad(item.value)}{item.label}
                </div>
              ))}
            </div>
          </div>

          {/* Apply Now 按钮 */}
          <Button
            onClick={() => {
              onClose();
              window.open('/creator-weekly', '_blank');
            }}
            className="w-full h-9 mt-auto rounded-full"
          >
            {t('rewardPlan.applyNow')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RewardModal;

